import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { logout, getUser } from '../services/auth';
import { getDealerOrders, cancelOrder, Order } from '../services/orders';
import { getImageUrl } from '../services/api';
import './DealerOrdersPage.css';
import BrandLogo from '../components/BrandLogo';

const DealerOrdersPage: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<Order[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [imageUrls, setImageUrls] = useState<{ [key: string]: string }>({});
  const navigate = useNavigate();
  const user = getUser();

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const data = await getDealerOrders();
      setOrders(data);
      setFilteredOrders(data);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredOrders(orders);
    } else {
      const query = searchQuery.toLowerCase().trim();
      const filtered = orders.filter(order => {
        const orderNumberMatch = order.orderNumber.toLowerCase().includes(query);
        const internalOrderNumberMatch = order.internalOrderNumber?.toLowerCase().includes(query);
        return orderNumberMatch || internalOrderNumberMatch;
      });
      setFilteredOrders(filtered);
    }
  }, [searchQuery, orders]);

  const handleOrderClick = async (order: Order) => {
    setSelectedOrder(order);
    // Lade Bild asynchron
    if (order.id && !imageUrls[order.id]) {
      try {
        const url = await getImageUrl(order.id);
        if (url) {
          setImageUrls(prev => ({ ...prev, [order.id]: url }));
        }
      } catch (error) {
        console.error('Error loading image:', error);
      }
    }
  };

  const handleLogout = () => {
    logout();
    window.location.href = '/';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('de-DE');
  };

  const getDeliveryLocation = (order: Order) => {
    if (order.deliveryType === 'SHOP') {
      return 'An den Shop';
    } else {
      return order.customerAddress || 'Kunde (Adresse nicht angegeben)';
    }
  };

  const getStatusLabel = (status: string) => {
    if (status === 'CANCELLED') return 'Storniert';
    if (status === 'COMPLETED' || status === 'SHIPPED') return 'Versendet';
    if (status === 'PARTS_ORDERED') return 'In Arbeit';
    return 'Ausstehend';
  };

  const handleCancelOrder = async (orderId: string) => {
    if (!selectedOrder) return;

    // Check if order can be cancelled (only PENDING)
    if (selectedOrder.status !== 'PENDING') {
      alert(
        'Diese Bestellung kann nicht mehr storniert werden, da sie bereits bearbeitet wird.\n\nBitte wenden Sie sich direkt an uns, wenn Sie Fragen haben.'
      );
      return;
    }

    if (
      !window.confirm(
        'Möchten Sie diese Bestellung wirklich stornieren? Diese Aktion kann nicht rückgängig gemacht werden.'
      )
    ) {
      return;
    }

    try {
      await cancelOrder(orderId, false);
      alert('Bestellung erfolgreich storniert');
      await loadOrders();
      setSelectedOrder(null);
    } catch (error: any) {
      console.error('Error cancelling order:', error);
      const errorMessage = error.response?.data?.error || 'Fehler beim Stornieren der Bestellung';
      alert(errorMessage);
    }
  };

  return (
    <div>
      <header className="app-header">
        <div className="container">
          <span style={{ marginRight: 'auto' }}>
            <BrandLogo height={17} withIcon />
          </span>
          <div className="header-actions">
            <button
              onClick={() => navigate('/orders/new')}
              className="btn btn-primary new-order-btn-desktop"
            >
              Neue Bestellung
            </button>
            <span className="user-info">Angemeldet als: {user?.username}</span>
            <button onClick={handleLogout} className="btn btn-secondary">
              Abmelden
            </button>
          </div>
        </div>
      </header>

      <div className="container">
        {loading ? (
          <div className="loading">Lade Bestellungen...</div>
        ) : (
          <>
            {orders.length === 0 ? (
              <div className="card">
                <p>Du hast noch keine Bestellungen aufgegeben.</p>
                <button onClick={() => navigate('/orders/new')} className="btn btn-primary">
                  Erste Bestellung aufgeben
                </button>
              </div>
            ) : (
              <>
                <div className="new-order-btn-mobile">
                  <button onClick={() => navigate('/orders/new')} className="btn btn-primary">
                    Neue Bestellung
                  </button>
                </div>
                <div className="search-container" style={{ marginBottom: '20px' }}>
                  <input
                    type="text"
                    placeholder="Suche nach Bestellnummer oder interner Auftragsnummer..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '16px',
                    }}
                  />
                </div>
                {/* Desktop Table View */}
                <div className="orders-table-container orders-table-desktop">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Vermerk</th>
                        <th>Bestellnummer</th>
                        <th>Produkt</th>
                        <th>Lieferort</th>
                        <th>Preis</th>
                        <th>Status</th>
                        <th>Datum</th>
                        <th>Aktionen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOrders.length === 0 ? (
                        <tr>
                          <td colSpan={8} style={{ textAlign: 'center', padding: '20px' }}>
                            {searchQuery
                              ? 'Keine Bestellungen gefunden'
                              : 'Keine Bestellungen vorhanden'}
                          </td>
                        </tr>
                      ) : (
                        filteredOrders.map(order => (
                          <tr key={order.id}>
                            <td>
                              {order.internalOrderNumber || (
                                <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>
                              )}
                            </td>
                            <td>
                              <strong>{order.orderNumber}</strong>
                            </td>
                            <td>{order.goggleModel?.name || 'Unbekannt'}</td>
                            <td>
                              <div className="delivery-location">{getDeliveryLocation(order)}</div>
                            </td>
                            <td>
                              {order.finalPrice !== undefined ? (
                                <div>
                                  <strong>{order.finalPrice.toFixed(2)} €</strong>
                                  {(order.discountPercent ?? 0) > 0 && (
                                    <div style={{ fontSize: '0.85em', color: '#666' }}>
                                      (Rabatt: {order.discountPercent}%)
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span style={{ color: '#999' }}>N/A</span>
                              )}
                            </td>
                            <td>
                              <span
                                className={`status-badge ${
                                  order.status === 'CANCELLED'
                                    ? 'cancelled'
                                    : order.status === 'COMPLETED' || order.status === 'SHIPPED'
                                    ? 'shipped'
                                    : order.status === 'PARTS_ORDERED'
                                      ? 'in-progress'
                                      : 'pending'
                                }`}
                              >
                                {getStatusLabel(order.status)}
                              </span>
                            </td>
                            <td>{formatDate(order.createdAt)}</td>
                            <td>
                              <button
                                onClick={() => handleOrderClick(order)}
                                className="btn btn-primary"
                              >
                                Details
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="orders-cards-container orders-cards-mobile">
                  {filteredOrders.length === 0 ? (
                    <div className="order-card-empty">
                      {searchQuery ? 'Keine Bestellungen gefunden' : 'Keine Bestellungen vorhanden'}
                    </div>
                  ) : (
                    filteredOrders.map(order => (
                      <div key={order.id} className="order-card">
                        <div className="order-card-header">
                          <div className="order-card-header-main">
                            <div className="order-card-number">
                              <strong>{order.orderNumber}</strong>
                            </div>
                            <span
                              className={`status-badge ${
                                order.status === 'CANCELLED'
                                  ? 'cancelled'
                                  : order.status === 'COMPLETED' || order.status === 'SHIPPED'
                                    ? 'shipped'
                                    : order.status === 'PARTS_ORDERED'
                                      ? 'in-progress'
                                      : 'pending'
                              }`}
                            >
                              {getStatusLabel(order.status)}
                            </span>
                          </div>
                          <button
                            onClick={() => handleOrderClick(order)}
                            className="btn btn-primary order-card-details-btn"
                          >
                            Details
                          </button>
                        </div>
                        <div className="order-card-body">
                          <div className="order-card-row">
                            <span className="order-card-label">Produkt:</span>
                            <span className="order-card-value">
                              {order.goggleModel?.name || 'Unbekannt'}
                            </span>
                          </div>
                          {order.internalOrderNumber && (
                            <div className="order-card-row">
                              <span className="order-card-label">Vermerk:</span>
                              <span className="order-card-value">{order.internalOrderNumber}</span>
                            </div>
                          )}
                          <div className="order-card-row">
                            <span className="order-card-label">Lieferort:</span>
                            <span className="order-card-value">{getDeliveryLocation(order)}</span>
                          </div>
                          {order.finalPrice !== undefined && (
                            <div className="order-card-row">
                              <span className="order-card-label">Preis:</span>
                              <span className="order-card-value">
                                <strong>{order.finalPrice.toFixed(2)} €</strong>
                                {(order.discountPercent ?? 0) > 0 && (
                                  <span
                                    style={{ fontSize: '0.85em', color: '#666', marginLeft: '5px' }}
                                  >
                                    (Rabatt: {order.discountPercent}%)
                                  </span>
                                )}
                              </span>
                            </div>
                          )}
                          <div className="order-card-row">
                            <span className="order-card-label">Datum:</span>
                            <span className="order-card-value">{formatDate(order.createdAt)}</span>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}

            {selectedOrder && (
              <div className="modal" onClick={() => setSelectedOrder(null)}>
                <div className="modal-content" onClick={e => e.stopPropagation()}>
                  <button className="modal-close" onClick={() => setSelectedOrder(null)}>
                    ×
                  </button>
                  <h2>Bestelldetails</h2>
                  <div className="order-details">
                    <p>
                      <strong>Bestellnummer:</strong> {selectedOrder.orderNumber}
                    </p>
                    {selectedOrder.internalOrderNumber && (
                      <p>
                        <strong>Vermerk:</strong> {selectedOrder.internalOrderNumber}
                      </p>
                    )}
                    <p>
                      <strong>Produkt:</strong> {selectedOrder.goggleModel?.name}
                    </p>
                    {selectedOrder.goggleModel?.description && (
                      <p>
                        <strong>Beschreibung:</strong> {selectedOrder.goggleModel.description}
                      </p>
                    )}
                    <p>
                      <strong>Produkttyp:</strong>{' '}
                      {selectedOrder.productType === 'CLIP' ? 'Clip' : 'Komplette Brille'}
                    </p>
                    {selectedOrder.glassType && (
                      <p>
                        <strong>Glas:</strong> {selectedOrder.glassType}
                      </p>
                    )}
                    {selectedOrder.bandType && (
                      <p>
                        <strong>Band:</strong> {selectedOrder.bandType}
                      </p>
                    )}
                    {selectedOrder.finalPrice !== undefined && (
                      <div
                        style={{
                          marginTop: '1rem',
                          padding: '1rem',
                          backgroundColor: '#f5f5f5',
                          borderRadius: '4px',
                        }}
                      >
                        <p>
                          <strong>Preisübersicht:</strong>
                        </p>
                        <p>Basispreis: {selectedOrder.basePrice?.toFixed(2) || '0.00'} €</p>
                        {(selectedOrder.discountPercent ?? 0) > 0 && (
                          <p>Rabatt: {selectedOrder.discountPercent}%</p>
                        )}
                        <p>
                          <strong>Endpreis: {selectedOrder.finalPrice.toFixed(2)} €</strong>
                        </p>
                      </div>
                    )}
                    <p>
                      <strong>Lieferung:</strong>{' '}
                      {selectedOrder.deliveryType === 'SHOP'
                        ? 'An den Shop'
                        : 'Direkt an den Kunden'}
                    </p>
                    {selectedOrder.customerAddress && (
                      <p>
                        <strong>Kundenadresse:</strong> {selectedOrder.customerAddress}
                      </p>
                    )}
                    {(() => {
                      const hasPrescriptionValues =
                        selectedOrder.leftEyeSph !== undefined ||
                        selectedOrder.leftEyeCyl !== undefined ||
                        selectedOrder.leftEyeA !== undefined ||
                        selectedOrder.rightEyeSph !== undefined ||
                        selectedOrder.rightEyeCyl !== undefined ||
                        selectedOrder.rightEyeA !== undefined;

                      const fmt = (v?: number | null) =>
                        v === null || v === undefined ? '—' : v.toFixed(2).replace('.', ',');
                      const fmtA = (v?: number | null) =>
                        v === null || v === undefined ? '—' : `${v}°`;

                      if (!hasPrescriptionValues) return null;

                      return (
                        <div
                          style={{
                            marginTop: '1rem',
                            padding: '1rem',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '4px',
                          }}
                        >
                          <h4 style={{ margin: '0 0 0.5rem 0' }}>Brillenpass-Werte</h4>
                          <div
                            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}
                          >
                            <div>
                              <strong>Linkes Auge</strong>
                              <div style={{ marginTop: '0.35rem', fontSize: '0.95em' }}>
                                <div>Sph: {fmt(selectedOrder.leftEyeSph)}</div>
                                <div>Cyl: {fmt(selectedOrder.leftEyeCyl)}</div>
                                <div>A: {fmtA(selectedOrder.leftEyeA)}</div>
                              </div>
                            </div>
                            <div>
                              <strong>Rechtes Auge</strong>
                              <div style={{ marginTop: '0.35rem', fontSize: '0.95em' }}>
                                <div>Sph: {fmt(selectedOrder.rightEyeSph)}</div>
                                <div>Cyl: {fmt(selectedOrder.rightEyeCyl)}</div>
                                <div>A: {fmtA(selectedOrder.rightEyeA)}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                    <p>
                      <strong>Status:</strong> {getStatusLabel(selectedOrder.status)}
                    </p>
                    {selectedOrder.notes && (
                      <div>
                        <p>
                          <strong>Notizen:</strong>
                        </p>
                        <div className="notes-content">{selectedOrder.notes}</div>
                      </div>
                    )}
                    <p>
                      <strong>Erstellt am:</strong> {formatDate(selectedOrder.createdAt)}
                    </p>
                    {selectedOrder.updatedAt !== selectedOrder.createdAt && (
                      <p>
                        <strong>Zuletzt aktualisiert:</strong> {formatDate(selectedOrder.updatedAt)}
                      </p>
                    )}
                    <p>
                      <strong>Rezeptbild:</strong>
                    </p>
                    <div
                      style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #ddd' }}
                    >
                      <button
                        onClick={() => handleCancelOrder(selectedOrder.id)}
                        className={`btn ${selectedOrder.status === 'PENDING' ? 'btn-danger' : 'btn-secondary'}`}
                        disabled={selectedOrder.status !== 'PENDING'}
                        style={{
                          opacity: selectedOrder.status === 'PENDING' ? 1 : 0.5,
                          cursor: selectedOrder.status === 'PENDING' ? 'pointer' : 'not-allowed',
                          width: '100%',
                        }}
                      >
                        {selectedOrder.status === 'PENDING'
                          ? 'Bestellung stornieren'
                          : 'Stornierung nicht möglich'}
                      </button>
                    </div>
                    <div className="prescription-image">
                      {imageUrls[selectedOrder.id] ? (
                        <img
                          src={imageUrls[selectedOrder.id]}
                          alt="Rezept"
                          style={{
                            maxWidth: '100%',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                          }}
                          onError={e => {
                            console.error('Error loading image:', e);
                            (e.target as HTMLImageElement).style.display = 'none';
                            const parent = (e.target as HTMLImageElement).parentElement;
                            if (parent) {
                              parent.innerHTML =
                                '<p style="color: red;">Fehler beim Laden des Bildes. Bitte versuche es später erneut.</p>';
                            }
                          }}
                        />
                      ) : (
                        <p>Lade Bild...</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DealerOrdersPage;
